import type {
  DateRange,
  RevenueReport,
  SnapshotsReport,
} from '@witmetrics/utils';
import { TIME_PERIOD } from '@witmetrics/utils/constants';
import {
  addYears,
  currentDate,
  customFormatDate,
  getLastYear,
  getMonth,
  getMonthToDate,
  getYear,
  getYearToDate,
} from '@witmetrics/utils/time';
import { sortNumericalByKey } from '@witmetrics/utils/arrays';
import { API } from '@/api';
import type { Comparison } from '@/types/metrics';
import { buildMetricRow, getBarWidth } from '@/utils/metrics';
import { formatTreatmentLabel } from '@/utils/treatments';
import type {
  MonthToDateReports,
  OverviewMetricRow,
  OverviewReports,
  TopTreatment,
} from './types';
import {
  KEY_METRIC_SNAPSHOTS,
  KEY_METRICS,
  OVERVIEW_METRIC,
  TOP_TREATMENTS_LIMIT,
} from './constants';

const { REVENUE_ACCEPTED, ACCEPTED_PATIENTS } = OVERVIEW_METRIC;

export const classes = {
  wrapper:
    'w-full max-w-[1600px] min-w-[700px] flex flex-col gap-[18px] p-8 outline-none',
  section: 'shrink-0',
  treatmentsRow: 'grid grid-cols-2 gap-[18px] shrink-0',
  revenueFill: 'bg-grad-cool',
};

function getMonthToDateRanges(): Comparison<DateRange> {
  const current = getMonthToDate();
  return {
    current,
    previous: [addYears(current[0], -1), addYears(current[1], -1)],
  };
}

function fetchMonthlyRevenue(
  practiceId: number,
  [startDate, endDate]: DateRange
) {
  return API.Reports.fetchPracticeRevenueReport(practiceId, {
    startDate,
    endDate,
    timePeriod: TIME_PERIOD.MONTH,
    filters: [],
    split: [],
  });
}

function fetchRevenueTotal(
  practiceId: number,
  [startDate, endDate]: DateRange
) {
  return API.Reports.fetchPracticeRevenueReport(practiceId, {
    startDate,
    endDate,
    timePeriod: TIME_PERIOD.ALL,
    filters: [],
    split: [],
  });
}

function fetchSnapshots(practiceId: number, [startDate, endDate]: DateRange) {
  return API.Reports.fetchSnapshotsReport(practiceId, {
    startDate,
    endDate,
    timePeriod: TIME_PERIOD.ALL,
    metrics: KEY_METRIC_SNAPSHOTS,
  });
}

export async function fetchOverviewReports(
  practiceId: number
): Promise<OverviewReports> {
  const monthToDate = getMonthToDateRanges();
  const [
    current,
    previous,
    previousRevenue,
    currentSnapshots,
    previousSnapshots,
  ] = await Promise.all([
    fetchMonthlyRevenue(practiceId, getYearToDate()),
    fetchMonthlyRevenue(practiceId, getLastYear()),
    fetchRevenueTotal(practiceId, monthToDate.previous),
    fetchSnapshots(practiceId, monthToDate.current),
    fetchSnapshots(practiceId, monthToDate.previous),
  ]);
  return {
    current,
    previous,
    monthToDate: {
      previousRevenue,
      snapshots: { current: currentSnapshots, previous: previousSnapshots },
    },
  };
}

function getCurrentMonthLeads(report: RevenueReport) {
  const month = getMonth(currentDate());
  return report.data.find((d) => getMonth(d.startDate) === month)?.leads;
}

function getSnapshotMetrics(report: SnapshotsReport) {
  return report.data[0]?.metrics ?? {};
}

export function buildKeyMetrics({
  current,
  monthToDate,
}: OverviewReports): OverviewMetricRow[] {
  const currentLeads = getCurrentMonthLeads(current);
  const previousLeads = monthToDate.previousRevenue.data[0]?.leads;
  const snapshots = {
    current: getSnapshotMetrics(monthToDate.snapshots.current),
    previous: getSnapshotMetrics(monthToDate.snapshots.previous),
  };
  const values: Record<OverviewMetricRow['key'], Comparison<number | null>> = {
    [REVENUE_ACCEPTED]: {
      current: currentLeads?.revenue.value ?? 0,
      previous: previousLeads?.revenue.value ?? 0,
    },
    [ACCEPTED_PATIENTS]: {
      current: currentLeads?.treatmentAccepted ?? 0,
      previous: previousLeads?.treatmentAccepted ?? 0,
    },
    NEW_LEADS: {
      current: snapshots.current.NEW_LEADS ?? null,
      previous: snapshots.previous.NEW_LEADS ?? null,
    },
    LEAD_TO_SALE_CONVERSION: {
      current: snapshots.current.LEAD_TO_SALE_CONVERSION ?? null,
      previous: snapshots.previous.LEAD_TO_SALE_CONVERSION ?? null,
    },
  };
  return KEY_METRICS.map((definition) => {
    const { current: currentValue, previous } = values[definition.key];
    return buildMetricRow(definition, currentValue, previous);
  });
}

export function buildPlaceholderKeyMetrics(): OverviewMetricRow[] {
  return KEY_METRICS.map((definition) =>
    buildMetricRow(definition, null, null)
  );
}

export function buildKeyMetricsSubtitle() {
  const { current, previous } = getMonthToDateRanges();
  const from = customFormatDate(current[0], 'd');
  const to = customFormatDate(current[1], 'd MMMM yyyy');
  return `Month to date, ${from} to ${to} against the same days in ${getYear(previous[0])}`;
}

export function buildPeriodTags(): Comparison<string> {
  const { current, previous } = getMonthToDateRanges();
  return {
    current: customFormatDate(current[0], 'MMM yy').toUpperCase(),
    previous: customFormatDate(previous[0], 'MMM yy').toUpperCase(),
  };
}

function sumTreatments(report: RevenueReport, key: 'count' | 'revenue') {
  const totals = new Map<number, { id: number; name: string; value: number }>();
  report.data.forEach(({ treatments }) => {
    treatments.forEach((treatment) => {
      const value = key === 'count' ? treatment.count : treatment.revenue.value;
      const entry = totals.get(treatment.id) ?? {
        id: treatment.id,
        name: formatTreatmentLabel(treatment),
        value: 0,
      };
      entry.value += value;
      totals.set(treatment.id, entry);
    });
  });
  return Array.from(totals.values()).filter((t) => t.value > 0);
}

export function buildTopTreatments(
  report: RevenueReport,
  key: 'count' | 'revenue'
): TopTreatment[] {
  const ranked = sortNumericalByKey(sumTreatments(report, key), 'value').slice(
    0,
    TOP_TREATMENTS_LIMIT
  );
  const max = ranked[0]?.value ?? 0;
  return ranked.map((t) => ({
    ...t,
    width: Math.max(2, getBarWidth(t.value, max)),
  }));
}
